import { notifications } from '@mantine/notifications';
import { captureException } from '@sentry/react';
import { IconAlertCircle } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import React, { Component } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Message to display to the user in case of an error.
   */
  userErrorMessage?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: undefined, errorInfo: undefined };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }

    // Send error details to Sentry
    captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });

    // Update state with error details
    this.setState({ errorInfo });

    // Show Mantine error notification
    notifications.show({
      title: 'Error',
      message: this.props.userErrorMessage ?? 'Something went wrong.',
      color: 'red',
      icon: <IconAlertCircle size="1rem" />,
      autoClose: false, // Keep the notification visible until user dismisses it
    });
  }

  render() {
    // We're just rendering a Mantine notification in case of an error,
    // so we don't need to render anything specific here.
    return this.props.children;
  }
}

export default ErrorBoundary;
